import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

const LAST_UPDATED = '2026-05-26';
const DPA_VERSION = '1.0';

/**
 * Data Processing Agreement (DPA) page.
 *
 * B2B procurement teams require a signed DPA under GDPR Article 28 when EAA Proof acts as
 * a processor on their behalf (scanning their site, generating reports). This page hosts the
 * standard agreement so prospects can review BEFORE the sales call — saves 1-2 weeks of
 * back-and-forth on legal redlines.
 *
 * Strategy: keep the standard processing terms in this template. For Enterprise customers
 * who insist on a counter-signed PDF, email support@eaaproof.com and we'll countersign within
 * 5 business days — that flow is intentional friction filter so we don't run a free-form
 * legal review queue for every sign-up.
 *
 * BEFORE GOING TO B2B ENTERPRISE PROCUREMENT: have this page reviewed by a GDPR-competent
 * lawyer once your legal entity is finalised. Standard EU SCCs (Commission Decision 2021/914)
 * are appended by reference, not inlined here. The current text is a solid SMB-grade default,
 * but enterprise procurement teams will redline anything that hasn't been through legal.
 */
export default function DpaPage() {
  useSeo({
    title: 'Data Processing Agreement',
    canonicalPath: '/dpa',
    description:
      "EAA Proof's standard Data Processing Agreement (DPA) under GDPR Article 28. Sub-processors, data categories, security measures, retention, and the right of audit are all documented.",
  });

  return (
    <div className="page legal-page">
      <header>
        <h1>Data Processing Agreement</h1>
        <p className="muted">
          Version {DPA_VERSION} · Last updated {LAST_UPDATED}
        </p>
      </header>

      <section>
        <p>
          This Data Processing Agreement ("DPA") forms part of the agreement between EAA Proof
          ("<strong>Processor</strong>", "we") and the customer ("<strong>Controller</strong>",
          "you") whenever EAA Proof processes personal data on your behalf under GDPR (Regulation
          (EU) 2016/679). It applies to every EAA Proof account that scans a website containing
          personal data, including but not limited to author names, customer testimonials,
          comments, contact information, and any personal data rendered in the page DOM at scan
          time.
        </p>
        <p>
          By creating an account and running scans, you agree to this DPA. For Enterprise
          customers requiring a counter-signed PDF, email{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> with the subject line
          "DPA countersignature request" — we'll respond within 5 business days.
        </p>
      </section>

      <section>
        <h2>1. Subject matter and duration</h2>
        <p>
          <strong>Subject matter:</strong> processing of personal data contained in the public
          DOM of websites you submit for accessibility scanning, plus your account profile
          information.
        </p>
        <p>
          <strong>Duration:</strong> the DPA remains in effect for as long as you have an
          active EAA Proof account, plus the data retention periods specified below.
        </p>
      </section>

      <section>
        <h2>2. Nature and purpose of processing</h2>
        <ul>
          <li>Loading public pages on URLs you submit, in a Microsoft Playwright-controlled Chromium browser.</li>
          <li>Running axe-core 4.10 accessibility analysis against the rendered DOM.</li>
          <li>Sending violation snippets (HTML fragments around each axe-core finding) to Anthropic Claude API for plain-English remediation text generation.</li>
          <li>Storing the resulting audit PDF and accessibility statement HTML in Cloudflare R2 (EU jurisdiction).</li>
          <li>Sending you transactional emails about scan completion, billing, and security notices.</li>
        </ul>
      </section>

      <section>
        <h2>3. Categories of data subjects and personal data</h2>
        <p>
          <strong>Data subjects:</strong> (a) you and the team members you invite to your
          EAA Proof account; (b) any natural person whose personal data is rendered in the
          public DOM of pages you scan.
        </p>
        <p>
          <strong>Personal data processed:</strong>
        </p>
        <ul>
          <li>Account data: email address, display name, Google OAuth subject id (if applicable).</li>
          <li>Billing data: customer name, billing address, VAT ID — processed by Stripe as joint processor.</li>
          <li>Scan content: whatever personal data appears in the DOM at the URL you submit. We do not select for this — we capture and analyse the page as a real browser sees it.</li>
        </ul>
        <p>
          <strong>Special category data (GDPR Art. 9):</strong> not processed unless you
          submit URLs whose DOM contains such data. We have no UI to ingest special categories
          directly. If your scan target contains health, biometric, or other special-category
          data, you confirm a valid Art. 9 lawful basis exists between you and the data
          subjects.
        </p>
      </section>

      <section>
        <h2>4. Sub-processors</h2>
        <p>
          You authorise EAA Proof to engage the following sub-processors. We commit to written
          contracts with each that impose data protection obligations equivalent to this DPA.
          You will be notified at <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>{' '}
          and via this page at least 30 days before any new sub-processor is added.
        </p>
        <table className="dpa-subprocessors">
          <thead>
            <tr>
              <th>Sub-processor</th>
              <th>Purpose</th>
              <th>Location</th>
              <th>Transfer mechanism</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Hetzner Online GmbH</td>
              <td>Application hosting + Postgres database</td>
              <td>Germany / Finland</td>
              <td>Intra-EU — no transfer mechanism needed</td>
            </tr>
            <tr>
              <td>Cloudflare, Inc.</td>
              <td>CDN, DNS, R2 object storage for scan reports</td>
              <td>EU jurisdiction (R2 EU bucket)</td>
              <td>EU SCCs (Commission Decision 2021/914) + Cloudflare DPA</td>
            </tr>
            <tr>
              <td>Anthropic, PBC</td>
              <td>AI-generated remediation text (Claude Sonnet API)</td>
              <td>United States</td>
              <td>EU SCCs (Module 2: Controller to Processor)</td>
            </tr>
            <tr>
              <td>Stripe Payments Europe Limited</td>
              <td>Payments, billing, tax</td>
              <td>Ireland (EU)</td>
              <td>Intra-EU — Stripe is joint controller for payment data</td>
            </tr>
            <tr>
              <td>Google LLC (OAuth, Analytics)</td>
              <td>Sign-in (only when user chooses Google sign-in); GA4 web analytics</td>
              <td>EU + United States</td>
              <td>EU-US Data Privacy Framework + EU SCCs</td>
            </tr>
            <tr>
              <td>Sentry, Inc.</td>
              <td>Error monitoring</td>
              <td>EU (eu.sentry.io)</td>
              <td>Intra-EU + EU SCCs as fallback</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>5. International transfers</h2>
        <p>
          Scan content (the rendered DOM of pages you submit) is transmitted to Anthropic in
          the United States for remediation text generation. This transfer is covered by EU
          Standard Contractual Clauses (Module 2) executed between EAA Proof and Anthropic.
        </p>
        <p>
          You acknowledge that the page DOM may contain personal data and that this transfer
          is necessary for performance of the contract (i.e. providing the remediation
          guidance you bought). Per Anthropic's published commercial API terms, the transmitted
          page content is not used to train models and is not retained beyond the request
          lifecycle.
        </p>
      </section>

      <section>
        <h2>6. Security measures (GDPR Art. 32)</h2>
        <p>
          The following technical and organisational measures are implemented and maintained
          throughout the term:
        </p>
        <ul>
          <li>TLS 1.2+ for all data in transit (HSTS preload enabled).</li>
          <li>Encryption at rest (database disk encryption; R2 server-side encryption).</li>
          <li>Strong authentication: ASP.NET Identity PasswordHasher (PBKDF2-HMAC-SHA256, 100,000 iterations) or Google OAuth; session cookies HttpOnly + Secure + SameSite=Lax.</li>
          <li>Role-based access control on the production database. Ownership checks return 404 on every request (no resource ID enumeration).</li>
          <li>Content Security Policy, HSTS, X-Frame-Options, Permissions-Policy enabled on every response.</li>
          <li>Stripe-provided PCI DSS scope reduction — we never receive card numbers.</li>
          <li>Webhook idempotency on financial events at two layers (StripeWebhookEvent.ProcessedAt + DB-enforced unique index on credit_transactions.StripeEventId).</li>
          <li>Quarterly backup verification (Hetzner snapshot restore).</li>
          <li>Internal change-management: every code change goes through PR review and CI before deployment.</li>
        </ul>
      </section>

      <section>
        <h2>7. Data subject rights</h2>
        <p>
          You remain the controller for data your customers entrust to your site. We assist you
          with data subject requests (access, rectification, erasure, restriction, portability,
          objection) by providing:
        </p>
        <ul>
          <li>Account data export on written request, within 30 days, free of charge.</li>
          <li>Account deletion via the Customer Portal (Stripe) and email request, processed within 30 days.</li>
          <li>A list of scan logs and their R2 storage keys so you can issue precise deletion requests for embedded personal data.</li>
        </ul>
        <p>
          Requests should be sent to <a href="mailto:privacy@eaaproof.com">privacy@eaaproof.com</a>.
        </p>
      </section>

      <section>
        <h2>8. Personal data breaches</h2>
        <p>
          We notify you without undue delay and in any case within 72 hours of becoming aware
          of a personal data breach affecting your data. The notification will describe (a) the
          nature of the breach, (b) the categories and approximate number of data subjects
          and records concerned, (c) the likely consequences, and (d) the measures we have
          taken or propose to take.
        </p>
      </section>

      <section>
        <h2>9. Audits</h2>
        <p>
          Once per twelve-month period and on at least 30 days' written notice, you may audit
          our compliance with this DPA. To minimise disruption, audits are conducted by
          desk-review of our published documentation (this DPA, our privacy policy, our
          security-relevant codepaths in the open-licensed parts of the code) and a Q&A call.
          On-premises inspections require an additional 60-day notice and are at your cost.
        </p>
      </section>

      <section>
        <h2>10. Retention and deletion</h2>
        <p>
          <strong>Scan reports:</strong> retained in your account for 12 months from generation.
          You can re-download at any time during this period.
        </p>
        <p>
          <strong>Account data:</strong> retained for the lifetime of your account plus 90 days
          after deletion (legal hold window for chargebacks and tax record retention).
        </p>
        <p>
          <strong>Billing records:</strong> retained for the period required by EU VAT and
          national accounting law in the operator's jurisdiction. For EAA Proof (Bulgarian
          operator) this is 5 years for tax records and 10 years for accounting records
          under the Bulgarian Accounting Act, aligned with the 10-year VAT-invoice retention
          rule applied across most EU member states.
        </p>
        <p>
          On termination of your account, all scan reports and account data are deleted within
          90 days, except billing records subject to the legal retention above. R2 object
          deletions cascade to all backups within 35 days.
        </p>
      </section>

      <section>
        <h2>11. Conflict</h2>
        <p>
          If anything in this DPA conflicts with the <Link to="/terms">Terms of Service</Link>{' '}
          or <Link to="/privacy">Privacy Policy</Link>, this DPA controls for matters of
          processor obligations under GDPR Art. 28. For everything else, the Terms of Service
          control.
        </p>
      </section>

      <section className="legal-related">
        <h2>Related</h2>
        <p>
          <Link to="/privacy">Privacy policy</Link> ·{' '}
          <Link to="/terms">Terms of service</Link> ·{' '}
          <Link to="/refund-policy">Refund policy</Link>
          {/* · <Link to="/imprint">Imprint</Link> — re-enable when imprint page is filled in */}
        </p>
      </section>
    </div>
  );
}
